import { isFunction } from 'lodash';
import DOMPurify from 'dompurify';
import { parse } from '../../../../../../node_module_clones/sheetclip/sheetclip';
import convertHtmlToDraft from '../../../../../../common/utils/editor/convertHtmlToDraft';
import { stringifyCellContent } from '../../../../../../common/table/utils/tableCellContentStringUtils';
import globalLogger from '../../../../../logger';
import { DataTransferTypes } from '../../../../../../common/media/mimeConstants';
import { TableChangeSource } from '../../../../../../common/table/TableTypes';

const logger = globalLogger.createChannel('table.dataUtils');

const HANDSONTABLE_HTML_GENERATOR_REGEXP = /handsontable/i;
const TABLE_HTML_REGEX = /<table/i;

/**
 * Encodes a literal trailing space in Handsontable cell HTML as `&nbsp;`.
 *
 * Handsontable trims a cell's raw HTML before decoding entities, so `<td>Text </td>` loses its final space,
 * while `<td>Text&nbsp;</td>` retains it and is decoded back to a space in the pasted cell value.
 *
 * This is important as the pasted text must exactly match the copied text in order for
 * the full cell data (e.g. text styling) to be pasted properly.
 */
const preserveHandsontableTrailingCellSpaces = (html: string): string => {
    const containerElement = document.createElement('div');
    containerElement.innerHTML = html;

    containerElement.querySelectorAll('td').forEach((cellElement) => {
        const lastChild = cellElement.lastChild;
        const lastChildText = lastChild?.nodeValue;
        const isTextNode = lastChild?.nodeType === 3;
        if (!isTextNode || lastChildText === null || lastChildText === undefined) return;

        lastChild.nodeValue = lastChildText.replace(/ $/, '\u00a0');
    });

    return containerElement.innerHTML;
};

/**
 * Pushes an entry into the next free index in a sparse row array.
 */
const pushToNextEmptyIndex = <T>(array: Array<T>, entry: T): number => {
    const index = array.findIndex((value) => value === undefined);
    if (index < 0) return array.push(entry) - 1;

    array[index] = entry;
    return index;
};

/**
 * Parses HTML table markup into a plain grid plus column widths.
 */
export const parseTableHTML = (html: string): { values: (string | null)[][]; colWidthsPx: number[] } | null => {
    if (!TABLE_HTML_REGEX.test(html)) return null;

    const containerElement = document.createElement('div');
    containerElement.innerHTML = html;
    const tableElement = containerElement.querySelector('table');

    if (!tableElement) return null;

    const colWidthsPx: number[] = [];
    tableElement.querySelectorAll('colgroup > col').forEach((colElement: unknown) => {
        if (colElement instanceof HTMLTableColElement) colWidthsPx.push(parseInt(colElement.width));
    });

    const rowElements = tableElement.querySelectorAll('tr');
    const values: (string | null)[][] = [];

    rowElements.forEach((rowElement, row) => {
        values[row] = values[row] || [];

        const colMapping: { [index: number]: number } = {};
        rowElement.querySelectorAll<HTMLTableCellElement>('td, th').forEach((entryElement, col) => {
            colMapping[col] = pushToNextEmptyIndex(
                values[row],
                stringifyCellContent(convertHtmlToDraft(entryElement.innerHTML)),
            );

            const colSpan = entryElement.colSpan || 0;
            for (let index = 2; index <= colSpan; index++) {
                pushToNextEmptyIndex(values[row], null);
            }

            const rowSpan = entryElement.rowSpan || 0;
            for (let index = 2; index <= rowSpan; index++) {
                const newRow = row + index - 1;
                const newCol = colMapping[col];
                values[newRow] = values[newRow] || [];
                values[newRow][newCol] = null;
            }
        });
    });

    if (values.length === 0) return null;

    return { values, colWidthsPx };
};

/**
 * Extracts table-shaped clipboard data from HTML first, then plain text as a fallback.
 */
export const parseTableHTMLFromClipboardData = (
    clipboardData: DataTransfer,
): { values: (string | null)[][]; colWidthsPx?: number[]; plainText?: string } | null => {
    if (!clipboardData) return null;

    try {
        const rawHtml = clipboardData.getData(DataTransferTypes.HTML);
        const htmlText = rawHtml && isFunction(DOMPurify?.sanitize) ? DOMPurify.sanitize(rawHtml) : rawHtml;

        const tableData = htmlText ? parseTableHTML(htmlText) : null;
        if (tableData) return tableData;
    } catch (error) {
        logger.error('Failed to parse the html using DOMPurify', error);
    }

    const plainText = clipboardData.getData(DataTransferTypes.PLAIN_TEXT);
    const values = parse(plainText);

    return { values };
};

/**
 * Sanitizes clipboard HTML for Handsontable and normalizes external table headers into data cells.
 *
 * Handsontable's default HTML table parser treats `<thead>/<th>` as header metadata rather than pasted row data.
 * That means external tables copied from places like Wikipedia lose their visible header rows on paste.
 *
 * Milanote wants those headers to paste into the grid as the first row, so for external HTML tables we rewrite
 * header cells to `<td>` before Handsontable parses the clipboard payload. Handsontable-originated clipboard HTML
 * needs its trailing literal cell spaces encoded, because its parser otherwise trims them.
 */
export const sanitizeClipboardHtmlForPaste = (html: string, source?: string): string => {
    const sanitizedHtml =
        html && isFunction(DOMPurify?.sanitize)
            ? DOMPurify.sanitize(html, {
                  ADD_TAGS: ['meta'],
                  ADD_ATTR: ['content'],
                  FORCE_BODY: true,
              })
            : html;

    if (source !== TableChangeSource.COPY_PASTE_PASTE || !TABLE_HTML_REGEX.test(sanitizedHtml)) {
        return sanitizedHtml;
    }

    const containerElement = document.createElement('div');
    containerElement.innerHTML = sanitizedHtml;

    const generatorElement = containerElement.querySelector('meta[name$="enerator"]');
    const generatorContent = generatorElement?.getAttribute('content') || '';

    if (HANDSONTABLE_HTML_GENERATOR_REGEXP.test(generatorContent)) {
        return preserveHandsontableTrailingCellSpaces(sanitizedHtml);
    }

    // Covert all th to be a td. This is to make sure that when copying from external sources,
    // the headers gets pasted correctly.
    containerElement.querySelectorAll('th').forEach((headerCell) => {
        const dataCell = document.createElement('td');

        Array.from(headerCell.attributes).forEach((attribute) => {
            dataCell.setAttribute(attribute.name, attribute.value);
        });

        dataCell.innerHTML = headerCell.innerHTML;
        headerCell.replaceWith(dataCell);
    });

    return containerElement.innerHTML;
};
